import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import useAuth from '@/domains/auth/hooks/useAuth';
import AdminOrderAlerts from '@/domains/dashboard-management/components/AdminOrderAlerts/AdminOrderAlerts';
import { useUserManagement } from '@/domains/system-management/hooks/useUserManagement';
import Header from '@/shared/components/layout/Header/Header';
import Sidebar from '@/shared/components/layout/Sidebar/Sidebar';
import type { NavbarItem } from '@/shared/components/layout/Sidebar/Sidebar.types';
import Seo from '@/shared/components/ui/Seo/Seo';
import { usePreferenceContext } from '@/shared/context/preferenceContext';
import useCommonCodes from '@/shared/hooks/useCommonCodes';
import { fetchTranslation } from '@/shared/utils/i18n';
import { loginPathFor } from '@/shared/utils/loginRedirect';
import { isRouteAuthorized, UNAUTHORIZED_ROUTE } from '@/shared/utils/navigation';
import { useAuthStore } from '@/store/authStore';
import { useLoadingStore } from '@/store/loadingStore';

export interface TitleMap {
  eng: string;
  // kor: string;
  ind: string;
}

const ProtectedRoutes = (): JSX.Element | null => {
  const location = useLocation();
  const navigate = useNavigate();
  const { preferenceData } = usePreferenceContext();
  const { i18n } = useTranslation();
  const { isLogin, user_id, lang: storedLang } = useAuthStore();
  const { useUser } = useUserManagement();
  const { data: userData } = useUser(user_id);

  const { handleAuthority } = useAuth({
    userID: user_id || null,
  });

  const [optionParams] = useState<string>('upper-code=021&upper-code=ORG');
  const { commonListData } = useCommonCodes({ optionParams });
  // Mobile sidebar drawer (below md); desktop ignores this and always shows the sidebar
  const [isSidebarOpen, setIsSidebarOpen] = useState<boolean>(false);

  // biome-ignore lint/correctness/useExhaustiveDependencies: redirect only when the session ends, not on every navigation
  useEffect(() => {
    if (!isLogin) {
      navigate(loginPathFor(location), { replace: true });
    }
  }, [isLogin, navigate]);

  // A page opens only when the authority list covers the tab/section that owns
  // it, so a url typed by hand lands on the unauthorized page instead of a
  // screen the user holds no grants for.
  useEffect(() => {
    if (!isLogin) return;
    if (isRouteAuthorized(location.pathname, handleAuthority)) return;
    navigate(UNAUTHORIZED_ROUTE, { replace: true });
  }, [isLogin, handleAuthority, location.pathname, navigate]);

  useEffect(() => {
    const initLanguage = async () => {
      const lang = storedLang || 'eng';

      const hasResource = i18n.hasResourceBundle(lang, 'translation');
      if (hasResource) {
        i18n.changeLanguage(lang);
        return;
      }

      const cached = localStorage.getItem(`translation_${lang}`);
      if (cached) {
        try {
          i18n.addResourceBundle(lang, 'translation', JSON.parse(cached), true, true);
          i18n.changeLanguage(lang);
          // Kamus cache bisa basi (tag baru di cm_language tidak pernah terbawa
          // tanpa logout); segarkan di background tanpa loader.
          fetchTranslation(lang);
          return;
        } catch {
          localStorage.removeItem(`translation_${lang}`);
        }
      }

      // Jalur lambat (fetch kamus dari backend saat state awal): tampilkan
      // loader global. Jalur cache di atas instan, tanpa kedip loader.
      const { setTranslationLoading } = useLoadingStore.getState();
      setTranslationLoading(true);
      try {
        await fetchTranslation(lang);
        await i18n.changeLanguage(lang);
      } finally {
        setTranslationLoading(false);
      }
    };

    initLanguage();
  }, [i18n, storedLang]);

  const titleMap: TitleMap = {
    eng: 'Toko Buku',
    // kor: 'Smart Safety',
    ind: 'Toko Buku',
  };
  const titleDashboard: TitleMap = {
    eng: 'Toko Buku',
    // kor: 'Smart Safety',
    ind: 'Toko Buku',
  };

  const isDashboard = location.pathname.startsWith('/store');

  // eng | ind only; anything else (leftover 'kor' session) falls back to eng
  const lang: keyof TitleMap = i18n.language === 'eng' ? 'ind' : 'eng';
  // Admin pages: titled, and kept out of search engines
  const seo = <Seo title={isDashboard ? titleDashboard[lang] : titleMap[lang]} noindex />;

  if (!userData || !preferenceData) {
    return null;
  }

  if (isDashboard) {
    return (
      <>
        {seo}
        <Outlet context={{ userInfo: userData, preferenceData: preferenceData }} />
      </>
    );
  }

  return (
    <div className="h-screen flex flex-col wrap-management">
      {seo}
      <AdminOrderAlerts />
      <div className="flex flex-row flex-1 overflow-hidden">
        <Sidebar
          userInfo={userData}
          commonData={commonListData}
          navbarList={handleAuthority}
          preferenceData={preferenceData}
          isOpen={isSidebarOpen}
          onClose={() => setIsSidebarOpen(false)}
        />

        <div className="flex flex-col flex-1 min-w-0">
          <Header onMenuClick={() => setIsSidebarOpen(true)} />
          <main className="flex-1 overflow-y-auto p-6 bg-[#F3F6FE]">
            <Outlet context={{ userInfo: userData, preferenceData }} />
          </main>
        </div>
      </div>
    </div>
  );
};

export default ProtectedRoutes;
